/**
 * Waterfall: each step floats from the running total (an invisible base bar
 * under a visible one); the last bar is the total.
 *
 * @module components/looker/charts/WaterfallTileChart
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { ResponsiveContainer, BarChart, Bar, Cell, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';
import type { LookerTile } from '@/services/looker/lookerTypes';
import { waterfallSteps } from '../lookerChartData';
import { AXIS_PROPS, CHART_HEIGHT, GRID_PROPS, TOOLTIP_PROPS, categoryTick, compactTick, fullNumber } from './chartTheme';

const KIND_COLOR = { up: '#34a853', down: '#ea4335', total: '#1a73e8' } as const;

export const WaterfallTileChart: React.FC<{ tile: LookerTile }> = ({ tile }) => {
  const { t } = useTranslation();
  const steps = waterfallSteps(tile, t('looker.waterfallTotal'));
  const category = categoryTick(t);
  return (
    <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
      <BarChart data={steps} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
        <CartesianGrid {...GRID_PROPS} />
        <XAxis dataKey="x" {...AXIS_PROPS} tickFormatter={category} minTickGap={8} />
        <YAxis {...AXIS_PROPS} tickFormatter={compactTick} width={48} />
        <Tooltip
          {...TOOLTIP_PROPS}
          labelFormatter={category}
          formatter={(_v: unknown, name: unknown, item: { payload?: { value?: number } }) =>
            name === 'size' ? [fullNumber(item.payload?.value), ''] : [null, null]}
        />
        <Bar dataKey="base" stackId="w" fill="transparent" isAnimationActive={false} legendType="none" />
        <Bar dataKey="size" stackId="w" radius={[4, 4, 4, 4]} maxBarSize={48} isAnimationActive={false}>
          {steps.map((s) => <Cell key={s.x} fill={KIND_COLOR[s.kind]} />)}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
};
